<template>
  <v-app class="fill-height">
    <v-app-bar
      id="appbar"
      app
      color="transparent"
      dark
      dense
      flat
      elevation="1"
    >
      <v-icon large>
        mdi-timer-outline
      </v-icon>
      <span
        class="text-h4 font-weight-medium ml-3 text-no-wrap"
      >Kronos&nbsp;<span class="text-caption">v{{ appVersion }}</span></span>
      <div class="ml-6 text-caption text-no-wrap">
        {{ licenceInfo.customer }} | Exp: {{ licenceInfo.expiry }}
      </div>
      <v-spacer />
      <v-btn
        :disabled="!licenceValid"
        color="white"
        class="indigo darken-4 mr-2"
        small
        @click="addJoker('task')"
      >
        Joker task
      </v-btn>
      <!-- <v-btn
        :disabled="journal.records.length === 0"
        color="white"
        class="indigo darken-4"
        @click="exportToCSV"
        small
        ><v-icon left>mdi-download</v-icon>Save {{name}}</v-btn
      > -->

      <v-btn
        :disabled="journal.records.length === 0"
        color="white"
        class="indigo darken-4"
        small
        @click="newSession"
      >
        <v-icon left>
          mdi-more
        </v-icon>New session
      </v-btn>

      <v-dialog
        v-model="notifications"
        max-width="290"
      >
        <v-card>
          <v-card-title class="headline">
            <v-icon
              x-large
              class="mr-3"
              color="error"
            >
              mdi-alert-circle
            </v-icon>Notification
          </v-card-title>
          <v-card-text class="mt-6 mb-6 text-center">
            Your session is not saved!
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              stext
              @click="
                notifications = false
                dialog = true
              "
            >
              Save
            </v-btn>
            <v-btn
              color="error"
              text
              @click="
                clearSession()
                notifications = false
              "
            >
              Don't save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- SAVE -->
      <v-dialog
        v-model="dialog"
        persistent
        max-width="290"
      >
        <!-- SAVE BUTTON-->
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            :disabled="journal.records.length === 0"
            color="white"
            class="indigo darken-4 ml-3"
            v-bind="attrs"
            small
            v-on="on"
          >
            <v-icon left>
              mdi-download
            </v-icon>Save
          </v-btn>
        </template>
        <!-- SAVE MODAL-->
        <v-card>
          <v-card-title>
            <span class="headline">Session Name</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="name"
              label="Name"
              required
              :rules="[rules.required, rules.name]"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="blue darken-1"
              text
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              :disabled="sanitize_tag(name).length === 0"
              color="blue darken-1"
              text
              @click="
                saveAndExport(name)
                dialog = false
              "
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-app-bar>

    <v-main class="fill-height">
      <v-container
        fluid
        fill-height
      >
        <!-- SHORTCUTS -->
        <v-row class="fill-height">
          <v-col
            id="shortcuts"
            :class="{ 'd-none': showSidebar }"
            class="pa-0 fill-height flex-grow-0"
          >
            <Shortcuts
              ref="shortcuts"
              :catalog="catalog"
              @expand="toggleSidebar"
              @quick:select="newFromEntry($event)"
              @quick:joker="newFromJoker($event)"
            />
          </v-col>
          <!-- SIDEBAR -->
          <v-col
            id="sidebar"
            :class="{ 'd-none': !showSidebar }"
            class="py-1"
          >
            <Sidebar
              ref="sidebar"
              :catalog="catalog"
              @catalog:load="onCatalogLoad"
              @collapse="toggleSidebar"
            />
          </v-col>
          <!-- MAIN (JOURNAL) -->
          <v-col
            id="main-view"
            class="py-1 flex-grow-1"
          >
            <Journal
              :catalog-values="catalogValues"
              @label:changed="syncRecordWithCatalog"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import Sidebar from '@/components/sidebar'
import Journal from '@/components/journal/main'
import Shortcuts from '@/components/shortcuts'
// import SETTINGS from '@/settings'
// import { getToken, pushData } from '@/utils/api'

import Catalog from '@/models/catalog'
import {
	validateLicenceInfo,
	isLicenceExpired,
} from '@/utils/licence'

export default {
	name: 'App',

	components: {
		Sidebar,
		// Main,
		Journal,
		Shortcuts,
	},

	data: () => ({
		catalog: new Catalog(),
		showSidebar: true,
		dialog: false,
		notifications: false,
		name: '',
		store: [],
		licenceValid: true,
		licenceMessage: '',
		licenceInfo: {
			customer: '',
			expiry: '',
			signature: '',
		},
		catalogFileName: '',
		rules: {
			required: value => !!value || 'Required.',
			name: value => {
				const pattern = /[a-zA-Z0-9_-]$/
				return pattern.test(value) || 'Invalid session name.'
			},
		},
	}),
	computed: {
		...mapState('journal', {
			journal: state => state.current,
		}),
		appVersion: function() {
			// return SETTINGS.VERSION
			return process.env.VUE_APP_VERSION
		},
		catalogValues() {
			return {
				cat1: this.getCatalogValues('cat1'),
				cat2: this.getCatalogValues('cat2'),
				cat3: this.getCatalogValues('cat3'),
				type: this.getCatalogValues('type'),
			}
		},
	},
	watch: {
		store: function(val) {
			// Mutate db localstorage
			localStorage.setItem('kronos-store', JSON.stringify(val))
		},
	},
mounted: function() {
	const storedLicence = localStorage.getItem('kronos-licence')

	if (storedLicence) {
		this.licenceInfo = JSON.parse(storedLicence)
	} else if (!this.requestLicence()) {
		return
	}

	if (!validateLicenceInfo(this.licenceInfo)) {
		this.licenceValid = false
		this.licenceMessage = 'Invalid licence signature.'
		alert(this.licenceMessage)
		return
	}

	if (isLicenceExpired(this.licenceInfo)) {
		this.licenceValid = false
		this.licenceMessage =
			'Licence expired on ' + this.licenceInfo.expiry
		alert(this.licenceMessage)
		return
	}

	this.sidebar = this.$refs.sidebar

	let db = localStorage.getItem('kronos-store')
	if (db !== null) this.store = JSON.parse(db)
},
methods: {
requestLicence() {
	const licenceText = prompt('Paste licence JSON')

	if (!licenceText) {
		this.licenceValid = false
		this.licenceMessage =
			'Aucune licence fournie. Merci d’importer une licence valide.'
		alert(this.licenceMessage)
		return false
	}

	try {
		const parsed = JSON.parse(licenceText)

		this.licenceInfo = parsed

		if (!validateLicenceInfo(this.licenceInfo)) {
			this.licenceValid = false
			this.licenceMessage = 'Invalid licence signature.'
			alert(this.licenceMessage)
			return false
		}

		if (isLicenceExpired(this.licenceInfo)) {
			this.licenceValid = false
			this.licenceMessage =
				'Licence expired on ' + this.licenceInfo.expiry
			alert(this.licenceMessage)
			return false
		}

		localStorage.setItem(
			'kronos-licence',
			JSON.stringify(parsed),
		)

		this.licenceValid = true
		this.licenceMessage = ''

		return true
	} catch (e) {
		this.licenceValid = false
		this.licenceMessage = 'Invalid licence JSON'
		alert(this.licenceMessage)
		return false
	}
},

		...mapActions('journal', ['resetJournal', 'addJoker']),

onCatalogLoad: function(payload) {
	if (!this.licenceValid) {
		if (!this.requestLicence()) {
			return false
		}
	}

	const csv = payload.csv
	this.catalogFileName = payload.fileName || ''

	this.catalog = new Catalog()

	const status = this.catalog.import(csv)
	if (status) {
		// either success or warning
	}
},
		toggleSidebar() {
			this.showSidebar = !this.showSidebar
		},
		deleteStore(name = '') {
			// Drop store localstorage
			if (name.length === 0) this.store = []
			let db = this.store.filter(x => x.name !== name)
			this.store = db
		},
		sanitize_tag(tag_name) {
			tag_name = tag_name || ''
			return tag_name.replace(/[^a-zA-Z0-9_-]/gi, '')
		},
		sanitize_text(text) {
			return text || ''
		},
		generateSessionId() {
			const now = new Date()
			const stamp = now.toISOString().replace(/[-:.TZ]/g, '')
			const random = Math.floor(1000 + Math.random() * 9000)

			return stamp + '_' + random
		},
		getSessionParts(name) {
			const rawParts = String(name || '').split('_')

			return {
				chp1: rawParts[0] || '',
				chp2: rawParts[1] || '',
				chp3: rawParts[2] || '',
				chp4: rawParts[3] || '',
				chp5: rawParts.slice(4).join('_') || '',
			}
		},
		syncRecordWithCatalog(record) {
			const entry = this.findCatalogEntryByLabel(record.entry.label)

			if (!entry) return

			record.entry.cats = [...entry.cats]
			record.entry.type = entry.type
			record.entry.code = entry.code || ''
			record.entry.description = entry.description || ''
			record.modified = true
		},
		findCatalogEntryByLabel(label) {
			if (!this.catalog || !this.catalog.entries) return undefined

			return this.catalog.entries.find(
				e =>
					e.label &&
					label &&
					e.label.toLowerCase().trim() === label.toLowerCase().trim(),
			)
		},
		getVA(code) {
			code = String(code || '')

			if (code.length < 2) return ''

			return code.substring(0, 2)
		},
		saveAndExport(name) {
			const savedSession = this.saveToStore(name)
			this.exportSessionToCSV(savedSession)
		},
		saveToStore(name) {
			name = this.sanitize_tag(name)

			// check if name exist
			let db = this.store.filter(x => x.name !== name)
			let obj = []

			this.journal.records.forEach(e =>
				obj.push({
					modified: e.modified ? true : false,
					cat1: this.sanitize_tag(e.entry.cats[0] || ''),
					cat2: this.sanitize_tag(e.entry.cats[1] || ''),
					cat3: this.sanitize_tag(e.entry.cats[2] || ''),
					type: this.sanitize_tag(e.entry.type),
					label: this.sanitize_tag(e.entry.label),
					code: this.sanitize_tag(e.code || e.entry.code),
					autostop: e.autostop ? 1 : 0,
					qty: parseInt(e.qty),
					timestamp_START: "'" + ((e.time.begin && e.time.begin.tm) || ''),
					timestamp_END: "'" + ((e.time.end && e.time.end.tm) || ''),
					'joker?': e.joker.toString(),
					description: this.sanitize_text(e.entry.description),
					comment: this.sanitize_text(e.comment),
				}),
			)

			const savedSession = {
				id: this.generateSessionId(),
				name: name,
				status: 0,
				datetime: new Date(),
				data: obj,
			}

			db.push(savedSession)
			this.store = db

			return savedSession
		},
		exportSessionToCSV(session) {
			if (!session || !session.data || session.data.length === 0) return false

			const sessionParts = this.getSessionParts(session.name)
			const exportDatetime = new Date().toISOString()

			const arr = [
				[
					'session_id',
					'session_name',
					'session_chp1',
					'session_chp2',
					'session_chp3',
					'session_chp4',
					'session_chp5',
					'export_datetime',
					'catalog_file',
					'app_version',
					'record_index',
					'modified',
					'cat1',
					'cat2',
					'cat3',
					'type',
					'label',
					'code',
					'va',
					'work_unit',
					'qty',
					'timestamp_start',
					'timestamp_end',
					'duration_ms',
					'duration_sec',
					'datetime_start_excel',
					'datetime_end_excel',
					'joker',
					'autostop',
					'description',
					'comment',
				],
				...session.data.map((e, index) => {
					const tsStart = parseInt(String(e.timestamp_START).replace("'", ''), 10)
					const tsEnd = parseInt(String(e.timestamp_END).replace("'", ''), 10)
					const durationMs =
						tsStart && tsEnd
							? tsEnd - tsStart
							: ''

					const durationSec =
						durationMs !== ''
							? (durationMs / 1000).toFixed(3).replace('.', ',')
							: ''

					const excelDateStart = tsStart
						? 25569 + Math.round(tsStart / 1000) / 86400
						: ''

					const excelDateEnd = tsEnd
						? 25569 + Math.round(tsEnd / 1000) / 86400
						: ''

					return [
						session.id || '',
						session.name,
						sessionParts.chp1,
						sessionParts.chp2,
						sessionParts.chp3,
						sessionParts.chp4,
						sessionParts.chp5,
						exportDatetime,
						this.catalogFileName || '',
						this.appVersion || '',
						index + 1,
						e.modified ? 'O' : 'N',
						e.cat1,
						e.cat2,
						e.cat3,
						e.type,
						e.label,
						e.code,
						this.getVA(e.code),
						this.getWorkUnit(e.code),
						e.qty,
						e.timestamp_START,
						e.timestamp_END,
						durationMs,
						durationSec,
						excelDateStart ? excelDateStart.toFixed(5).replace('.', ',') : '',
						excelDateEnd ? excelDateEnd.toFixed(5).replace('.', ',') : '',
						e['joker?'],
						e.autostop,
						e.description,
						e.comment,
					]
				}),
			]

			const csvContent = arr
				.map(row =>
					row
						.map(value =>
							`"${String(value === null || value === undefined ? '' : value).replace(/"/g, '""')}"`
						)
						.join(';'),
				)
				.join('\n')

			const blob = new Blob(['\uFEFF' + csvContent], {
				type: 'text/csv;charset=utf-8;',
			})
			const link = document.createElement('a')
			const url = URL.createObjectURL(blob)

			link.setAttribute('href', url)
			link.setAttribute('download', session.name + '.csv')
			link.style.visibility = 'hidden'

			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			URL.revokeObjectURL(url)

			return true
		},
		newSession() {
			// New session
			if (this.name) {
				let db = this.store.filter(x => x.name === this.name)
				if (db.length > 0) {
					this.saveToStore(this.name)
					this.clearSession()
					return
				}
			}
			this.notifications = true
		},
		clearSession() {
			//clear session
			this.name = null
			let file = document.querySelector('input[type=file]')
			file.value = null
			this.resetJournal() // see vuex
			this.catalog = new Catalog()
		},
		getWorkUnit(code) {
			code = String(code || '')
			const parts = code.split('_')
			const last = parts[parts.length - 1]

			if (!last) return ''

			// Unité d’œuvre uniquement si le suffixe est alphabétique : eng, pos, colis, etc.
			if (!/^[a-zA-Z]+$/.test(last)) return ''

			return last
		},
		getCatalogValues(field) {
			if (!this.catalog || !this.catalog.entries) return []

			const values = this.catalog.entries
				.map(e => {
					if (field === 'cat1') return e.cats[0] || ''
					if (field === 'cat2') return e.cats[1] || ''
					if (field === 'cat3') return e.cats[2] || ''
					if (field === 'type') return e.type || ''
					if (field === 'code') return e.code || ''
					if (field === 'label') return e.label || ''
					return ''
				})
				.filter(v => v)

			return [...new Set(values)].sort()
		},
		exportToCSV() {
			// generate the csv content
			// console.log(this.journal.records)
			const arr = [
				// first row
				[
					'cat1',
					'cat2',
					'cat3',
					'type',
					'label',
					'code',
					'qty',
					'begin timestamp',
					'begin UTC',
					'end timestamp',
					'end UTC',
					'duration',
					'joker?',
					'description',
					'comment',
				],
				// journal records
				...this.journal.records.map(e => [
					e.entry.cats[0],
					e.entry.cats[1],
					e.entry.cats[2],
					e.entry.type,
					e.entry.label,
					e.code || e.entry.code,
					e.qty,
					(e.time.begin && e.time.begin.time) || '',
					(e.time.begin && e.time.begin.utcStr) || '',
					(e.time.end && e.time.end.time) || '',
					(e.time.end && e.time.end.utcStr) || '',
					e.getDuration(),
					e.joker.toString(),
					e.entry.description,
					e.comment,
				]),
			]
			const csv =
				'data:text/csv;charset=utf-8,' +
				arr.map(e => '"' + e.join('","') + '"').join('\n')
			// generate a link and click it
			var encoded = encodeURI(csv)
			var link = document.createElement('a')
			link.setAttribute('href', encoded)
			link.setAttribute('download', 'journal.csv')
			document.body.appendChild(link) // Required for FF
			link.click() // This will download the data file
			document.body.removeChild(link)
			return true
		},
	},
}
</script>

<style lang="scss">
body { margin: 0; }
html, body {
  height: 100%;
  overscroll-behavior-y: none; // empêche le pull-to-refresh (Chrome/Android + la plupart modernes)
  overscroll-behavior-x: none;
}
// html {
//   overflow-y: hidden;
// }
body {
	overflow-y: hidden;
	height: 100vh;
}
// #app {
//   max-height: 100vh;
// }
// .v-main {
//   max-height: 100vh;
// }
#sidebar {
	overflow-y: hidden;
	max-height: 100%;
	max-width: 420px;
}

#main-view {
	overflow-y: hidden;
	max-height: 100%;
}

#appbar > .v-toolbar__content {
	background: linear-gradient(
		90deg,
		rgba(173, 20, 87, 1) 0%,
		rgba(41, 182, 246, 1) 100%
	);
}
</style>
